import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut, Key } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { Sidebar } from "@/components/Sidebar";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { DeviceManagement } from "@/components/profile/DeviceManagement";
import { PasswordReset } from "@/components/profile/PasswordReset";
import { Progress } from "@/components/ui/progress";
import { useAiQuota } from "@/hooks/useAiQuota";
import { useProfileStats } from "@/hooks/useProfileStats";
import { useMembershipStatus } from "@/hooks/useMembershipStatus";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

export default function Profile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const { theme, setTheme, systemTheme } = useTheme();
  const { data: aiQuota } = useAiQuota(user?.id);
  const { data: profileStats } = useProfileStats(user?.id);
  const { data: membership } = useMembershipStatus(user?.id);
  const membershipLabel = membership?.isPremium ? "会员" : "普通用户";
  const membershipSubLabel = membership?.isPermanent
    ? "永久"
    : membership?.premiumUntil
      ? new Date(membership.premiumUntil).toLocaleString("zh-CN", { hour12: false })
      : "-";
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const isLiveDemoMode = new URLSearchParams(location.search).get("live") === "1";
  const aiUnlimited = !!aiQuota?.isUnlimited;

  useEffect(() => {
    if (!isLiveDemoMode) return;
    const timer = window.setTimeout(() => {
      navigate("/?live=1", { replace: true });
    }, 6500 + Math.floor(Math.random() * 2500));
    return () => window.clearTimeout(timer);
  }, [isLiveDemoMode, navigate]);

  const handleLogout = async () => {
    await signOut();
    toast({ title: "已退出登录" });
    navigate("/");
  };

  const handleNavigate = (page: string) => {
    if (page === "home") navigate("/");
    else if (page === "vocabulary") navigate("/vocabulary");
    else if (page === "favorites") navigate("/favorites");
    else if (page === "history") navigate("/history");
    else if (page === "feedback") navigate("/feedback");
    else if (page === "profile") navigate("/profile");
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground mb-4">请先登录以查看个人中心</p>
            <Button onClick={() => navigate("/auth")}>去登录</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        currentPage="profile"
        onNavigate={handleNavigate}
        isLoggedIn={!!user}
        onLogout={handleLogout}
      />

      <main className={cn(
        "transition-all duration-300",
        sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
      )}>
        <div className="container px-6 py-8 max-w-4xl">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold text-foreground">个人中心</h1>
          </div>

          {/* Profile Card */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={user.user_metadata?.avatar_url} />
                  <AvatarFallback className="bg-primary/10 text-primary text-2xl">
                    {user.email?.charAt(0).toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-foreground">
                    {user.user_metadata?.nickname || user.email?.split("@")[0] || "用户"}
                  </h2>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                      {membershipLabel}
                    </span>
                    {membership?.isPremium && (
                      <span className="text-xs text-muted-foreground">
                        到期：{membershipSubLabel}
                      </span>
                    )}
                  </div>
                </div>
                <Dialog open={redeemOpen} onOpenChange={setRedeemOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      会员升级
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>输入卡密升级</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 pt-2">
                      <Input
                        placeholder="请输入卡密"
                        value={redeemCode}
                        onChange={(e) => setRedeemCode(e.target.value)}
                      />
                      <Button
                        className="w-full"
                        disabled={redeeming || !redeemCode.trim()}
                        onClick={async () => {
                          if (!redeemCode.trim()) return;
                          setRedeeming(true);
                          try {
                            const { data, error } = await supabase.functions.invoke("redeem-cdk", {
                              body: { code: redeemCode.trim() },
                            });
                            if (error) throw error;
                            if ((data as any)?.error) throw new Error((data as any).error);
                            toast({ title: "升级成功" });
                            setRedeemOpen(false);
                            setRedeemCode("");
                            await Promise.all([
                              queryClient.invalidateQueries({ queryKey: ["ai-quota", user.id] }),
                              queryClient.invalidateQueries({ queryKey: ["membership-status", user.id] }),
                            ]);
                          } catch (e) {
                            const msg = (e as any)?.context?.body?.error || (e as any)?.message || "升级失败";
                            toast({ title: "升级失败", description: msg, variant: "destructive" });
                          } finally {
                            setRedeeming(false);
                          }
                        }}
                      >
                        确认兑换
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground">会员与额度</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-muted-foreground">AI 查词剩余额度(每月1号重置)</span>
                    <span className="font-medium text-foreground">
                      {aiUnlimited ? "∞" : aiQuota ? `${aiQuota.lookupRemaining}/${aiQuota.lookupLimit}` : "--/--"}
                    </span>
                  </div>
                  <Progress
                    value={
                      aiUnlimited
                        ? 100
                        : aiQuota && aiQuota.lookupLimit > 0
                        ? (aiQuota.lookupRemaining / aiQuota.lookupLimit) * 100
                        : 0
                    }
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-muted-foreground">AI 解析剩余额度(每月1号重置)</span>
                    <span className="font-medium text-foreground">
                      {aiUnlimited ? "∞" : aiQuota ? `${aiQuota.analyzeRemaining}/${aiQuota.analyzeLimit}` : "--/--"}
                    </span>
                  </div>
                  <Progress
                    value={
                      aiUnlimited
                        ? 100
                        : aiQuota && aiQuota.analyzeLimit > 0
                        ? (aiQuota.analyzeRemaining / aiQuota.analyzeLimit) * 100
                        : 0
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold text-primary">{profileStats?.studyDays ?? 0}</div>
                <div className="text-sm text-muted-foreground">学习天数</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold text-primary">{profileStats?.vocabularyCount ?? 0}</div>
                <div className="text-sm text-muted-foreground">收藏单词</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold text-primary">{profileStats?.watchedVideos ?? 0}</div>
                <div className="text-sm text-muted-foreground">观看视频</div>
              </CardContent>
            </Card>
          </div>

          {/* Device Management */}
          <DeviceManagement />

          {/* Password Reset */}
          {showPasswordReset ? (
            <PasswordReset 
              userEmail={user.email || ""} 
              onClose={() => setShowPasswordReset(false)} 
            />
          ) : (
            <Card className="mb-6">
              <CardContent className="pt-6">
                <Button 
                  variant="ghost" 
                  className="w-full justify-start gap-3 h-12"
                  onClick={() => setShowPasswordReset(true)}
                >
                  <Key className="h-5 w-5" />
                  <span>修改密码</span>
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Theme Selection */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="mb-3">
                <h2 className="text-lg font-semibold text-foreground">主题设置</h2>
                <p className="text-sm text-muted-foreground">选择和系统一致，或手动选择黑夜 / 白天</p>
              </div>
              <RadioGroup
                value={theme || "system"}
                onValueChange={(v) => setTheme(v as "system" | "light" | "dark")}
                className="grid grid-cols-3 gap-3"
              >
                <div className="flex items-center space-x-2 border rounded-lg p-3">
                  <RadioGroupItem value="system" id="theme-system" />
                  <Label htmlFor="theme-system">跟随系统</Label>
                </div>
                <div className="flex items-center space-x-2 border rounded-lg p-3">
                  <RadioGroupItem value="light" id="theme-light" />
                  <Label htmlFor="theme-light">白天</Label>
                </div>
                <div className="flex items-center space-x-2 border rounded-lg p-3">
                  <RadioGroupItem value="dark" id="theme-dark" />
                  <Label htmlFor="theme-dark">黑夜</Label>
                </div>
              </RadioGroup>
              <p className="text-xs text-muted-foreground mt-2">
                当前：{(theme === "system" ? systemTheme : theme) || "system"}
              </p>
            </CardContent>
          </Card>

          {/* Logout */}
          <Card>
            <CardContent className="pt-6">
              <Button 
                variant="ghost" 
                className="w-full justify-start gap-3 h-12 text-destructive hover:text-destructive"
                onClick={handleLogout}
              >
                <LogOut className="h-5 w-5" />
                <span>退出登录</span>
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
